import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/config';
import { useAuth } from '../../contexts/AuthContext';
import { useConfirm } from '../../contexts/ConfirmContext';
import PageHeader from '../../components/layout/PageHeader';
import Card from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';
import { Plus, Edit, Trash2, Shield, Users, Lock, Unlock, CheckCircle2, AlertCircle } from 'lucide-react';

interface Permission {
  id: string;
  name: string;
  description: string;
  module: string;
  resource: string;
  action: 'create' | 'read' | 'update' | 'delete' | 'admin';
}

interface Role {
  id: string;
  name: string;
  description: string;
  is_system_role: boolean;
  permissions: string[];
  user_count: number;
  created_at: string;
  updated_at: string;
}

interface RoleAssignment {
  id: string;
  user_id: string;
  role_id: string;
  assigned_by: string;
  assigned_at: string;
  user_name: string;
  user_email: string;
  role_name: string;
}

const SYSTEM_PERMISSIONS = [
  // User Management
  { id: 'users.create', name: 'Create Users', description: 'Add new users to the system', module: 'User Management', resource: 'users', action: 'create' as const },
  { id: 'users.read', name: 'View Users', description: 'View user profiles and information', module: 'User Management', resource: 'users', action: 'read' as const },
  { id: 'users.update', name: 'Edit Users', description: 'Modify user profiles and settings', module: 'User Management', resource: 'users', action: 'update' as const },
  { id: 'users.delete', name: 'Delete Users', description: 'Remove users from the system', module: 'User Management', resource: 'users', action: 'delete' as const },
  
  // Role Management
  { id: 'roles.create', name: 'Create Roles', description: 'Create new roles and permissions', module: 'Role Management', resource: 'roles', action: 'create' as const },
  { id: 'roles.read', name: 'View Roles', description: 'View roles and permissions', module: 'Role Management', resource: 'roles', action: 'read' as const },
  { id: 'roles.update', name: 'Edit Roles', description: 'Modify roles and permissions', module: 'Role Management', resource: 'roles', action: 'update' as const },
  { id: 'roles.delete', name: 'Delete Roles', description: 'Remove custom roles', module: 'Role Management', resource: 'roles', action: 'delete' as const },
  
  // Grid Management
  { id: 'grids.create', name: 'Create Grids', description: 'Create new timetable grids', module: 'Grid Management', resource: 'grids', action: 'create' as const },
  { id: 'grids.read', name: 'View Grids', description: 'View timetable grids', module: 'Grid Management', resource: 'grids', action: 'read' as const },
  { id: 'grids.update', name: 'Edit Grids', description: 'Modify timetable grids', module: 'Grid Management', resource: 'grids', action: 'update' as const },
  { id: 'grids.delete', name: 'Delete Grids', description: 'Remove timetable grids', module: 'Grid Management', resource: 'grids', action: 'delete' as const },
  
  // Staff Allocation
  { id: 'allocations.create', name: 'Create Allocations', description: 'Assign staff to classes', module: 'Staff Allocation', resource: 'allocations', action: 'create' as const },
  { id: 'allocations.read', name: 'View Allocations', description: 'View staff allocations', module: 'Staff Allocation', resource: 'allocations', action: 'read' as const },
  { id: 'allocations.update', name: 'Edit Allocations', description: 'Modify staff allocations', module: 'Staff Allocation', resource: 'allocations', action: 'update' as const },
  { id: 'allocations.delete', name: 'Delete Allocations', description: 'Remove staff allocations', module: 'Staff Allocation', resource: 'allocations', action: 'delete' as const },
  
  // Reports
  { id: 'reports.read', name: 'View Reports', description: 'Access system reports', module: 'Reports', resource: 'reports', action: 'read' as const },
  { id: 'reports.admin', name: 'Admin Reports', description: 'Access administrative reports', module: 'Reports', resource: 'reports', action: 'admin' as const },
  
  // System Administration
  { id: 'system.admin', name: 'System Administration', description: 'Full system administration access', module: 'System', resource: 'system', action: 'admin' as const }
];

const RoleManagement: React.FC = () => {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [roles, setRoles] = useState<Role[]>([]);
  const [roleAssignments, setRoleAssignments] = useState<RoleAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'roles' | 'assignments'>('roles');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);

  // Form state for role creation/editing
  const [roleForm, setRoleForm] = useState({
    name: '',
    description: '',
    permissions: [] as string[]
  });

  useEffect(() => {
    loadRoles();
    loadRoleAssignments();
  }, []);

  const loadRoles = async () => {
    try {
      setLoading(true);
      
      // Get roles
      const { data: rolesData, error: rolesError } = await supabase
        .from('user_roles')
        .select('*')
        .order('name');

      if (rolesError) throw rolesError;

      // Get user counts for each role
      const { data: staffData, error: staffError } = await supabase
        .from('staff')
        .select('role');

      if (staffError) throw staffError;

      const roleCounts = staffData?.reduce((acc, staff) => {
        acc[staff.role] = (acc[staff.role] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) || {};

      const rolesWithCounts = rolesData?.map(role => ({
        ...role,
        user_count: roleCounts[role.name] || 0
      })) || [];

      setRoles(rolesWithCounts);
      setError(null);
    } catch (error) {
      console.error('Error loading roles:', error);
      setError(error instanceof Error ? error.message : 'Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const loadRoleAssignments = async () => {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select(`
          id,
          name,
          email,
          role,
          created_at,
          user_roles!inner(name)
        `)
        .order('name');

      if (error) throw error;

      const assignments: RoleAssignment[] = data?.map(staff => ({
        id: `${staff.id}_${staff.role}`,
        user_id: staff.id,
        role_id: staff.role,
        assigned_by: 'system', // Could be enhanced to track who assigned
        assigned_at: staff.created_at,
        user_name: staff.name,
        user_email: staff.email,
        role_name: staff.user_roles?.name || staff.role
      })) || [];

      setRoleAssignments(assignments);
    } catch (error) {
      console.error('Error loading role assignments:', error);
    }
  };

  const handleCreateRole = async () => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .insert([{
          name: roleForm.name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          is_system_role: false,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;

      setRoles([...roles, { ...data, user_count: 0 }]);
      setIsCreateModalOpen(false);
      resetRoleForm();
    } catch (error) {
      console.error('Error creating role:', error);
      setError(error instanceof Error ? error.message : 'Failed to create role');
    }
  };

  const handleUpdateRole = async () => {
    if (!editingRole) return;

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .update({
          name: roleForm.name,
          description: roleForm.description,
          permissions: roleForm.permissions,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingRole.id)
        .select()
        .single();

      if (error) throw error;

      setRoles(roles.map(r => r.id === editingRole.id ? { ...data, user_count: editingRole.user_count } : r));
      setEditingRole(null);
      resetRoleForm();
    } catch (error) {
      console.error('Error updating role:', error);
      setError(error instanceof Error ? error.message : 'Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    if (role.is_system_role) {
      setError('Cannot delete system roles');
      return;
    }

    if (role.user_count > 0) {
      setError('Cannot delete role that is assigned to users');
      return;
    }

    if (!(await confirm({
      title: 'Delete role?',
      message: `Are you sure you want to delete the role "${role.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    }))) {
      return;
    }

    try {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('id', roleId);

      if (error) throw error;

      setRoles(roles.filter(r => r.id !== roleId));
    } catch (error) {
      console.error('Error deleting role:', error);
      setError(error instanceof Error ? error.message : 'Failed to delete role');
    }
  };

  const handleAssignRole = async (userId: string, roleId: string) => {
    try {
      const { error } = await supabase
        .from('staff')
        .update({ role: roleId })
        .eq('id', userId);

      if (error) throw error;

      loadRoles();
      loadRoleAssignments();
      setIsAssignModalOpen(false);
    } catch (error) {
      console.error('Error assigning role:', error);
      setError(error instanceof Error ? error.message : 'Failed to assign role');
    }
  };

  const resetRoleForm = () => {
    setRoleForm({
      name: '',
      description: '',
      permissions: []
    });
  };

  const startEditRole = (role: Role) => {
    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description,
      permissions: role.permissions || []
    });
  };

  const togglePermission = (permissionId: string) => {
    setRoleForm({
      ...roleForm,
      permissions: roleForm.permissions.includes(permissionId)
        ? roleForm.permissions.filter(p => p !== permissionId)
        : [...roleForm.permissions, permissionId]
    });
  };

  const getPermissionsByModule = () => {
    const grouped = SYSTEM_PERMISSIONS.reduce((acc, permission) => {
      if (!acc[permission.module]) {
        acc[permission.module] = [];
      }
      acc[permission.module].push(permission);
      return acc;
    }, {} as Record<string, Permission[]>);
    
    return grouped;
  };

  const getRoleIcon = (role: Role) => {
    if (role.is_system_role) {
      return <Shield className="h-4 w-4 text-navy-600" />;
    }
    return <Users className="h-4 w-4 text-ink-500" />;
  };

  if (loading) {
    return (
      <div>
        <PageHeader
          title="Role Management"
          description="Create and manage custom roles with module-based permissions"
        />
        <div className="flex justify-center items-center h-64">
          <Spinner size="lg" label="Loading…" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Role Management"
        description="Create and manage custom roles with module-based permissions"
      />

      {error && (
        <div className="mb-6 bg-danger-bg border border-danger-border rounded-md p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-danger-fg" />
            <p className="text-danger-fg">{error}</p>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('roles')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'roles'
                ? 'border-navy-500 text-navy-600'
                : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
            }`}
          >
            Roles & Permissions
          </button>
          <button
            onClick={() => setActiveTab('assignments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'assignments'
                ? 'border-navy-500 text-navy-600'
                : 'border-transparent text-ink-500 hover:text-ink-700 hover:border-ink-300'
            }`}
          >
            Role Assignments
          </button>
        </nav>
      </div>

      {/* Roles Tab */}
      {activeTab === 'roles' && (
        <>
          {/* Controls */}
          <Card className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-card-title text-ink-900">System Roles</h3>
                <p className="text-sm text-ink-500">Manage roles and their permissions</p>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-navy-600 text-white rounded-md hover:bg-navy-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create Role
              </button>
            </div>
          </Card>

          {/* Roles List */}
          <Card>
            <div className="divide-y divide-ink-200">
              {roles.map((role) => (
                <div key={role.id} className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {getRoleIcon(role)}
                      <div>
                        <h4 className="text-card-title text-ink-900 flex items-center gap-2">
                          {role.name}
                          {role.is_system_role && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
                              System
                            </span>
                          )}
                        </h4>
                        <p className="text-sm text-ink-500">{role.description}</p>
                        <p className="font-mono tabular-nums text-xs text-ink-400 mt-1">
                          {role.user_count} user{role.user_count !== 1 ? 's' : ''} assigned
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEditRole(role)}
                        disabled={role.is_system_role}
                        className="text-navy-600 hover:text-navy-900 p-1 disabled:text-ink-400 disabled:cursor-not-allowed"
                        title={role.is_system_role ? 'Cannot edit system roles' : 'Edit role'}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRole(role.id)}
                        disabled={role.is_system_role || role.user_count > 0}
                        className="text-danger-fg hover:text-danger-fg p-1 disabled:text-ink-400 disabled:cursor-not-allowed"
                        title={
                          role.is_system_role 
                            ? 'Cannot delete system roles' 
                            : role.user_count > 0 
                            ? 'Cannot delete role with assigned users'
                            : 'Delete role'
                        }
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  {/* Permissions */}
                  <div className="mt-4">
                    <h5 className="text-sm font-medium text-ink-700 mb-2">Permissions:</h5>
                    <div className="flex flex-wrap gap-2">
                      {(role.permissions || []).map(permissionId => {
                        const permission = SYSTEM_PERMISSIONS.find(p => p.id === permissionId);
                        return (
                          <span
                            key={permissionId}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-ok-bg text-ok-fg"
                          >
                            {permission?.name || permissionId}
                          </span>
                        );
                      })}
                      {(!role.permissions || role.permissions.length === 0) && (
                        <span className="text-sm text-ink-500">No permissions assigned</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </>
      )}

      {/* Assignments Tab */}
      {activeTab === 'assignments' && (
        <>
          {/* Controls */}
          <Card className="mb-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-card-title text-ink-900">Role Assignments</h3>
                <p className="text-sm text-ink-500">View and manage user role assignments</p>
              </div>
              <button
                onClick={() => setIsAssignModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-ok-solid text-white rounded-md hover:bg-ok-solid transition-colors"
              >
                <Plus className="h-4 w-4" />
                Assign Role
              </button>
            </div>
          </Card>

          {/* Assignments List */}
          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-ink-200">
                <thead className="bg-ink-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Assigned Date
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-ink-200">
                  {roleAssignments.map((assignment) => (
                    <tr key={assignment.id} className="hover:bg-ink-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-ink-900">{assignment.user_name}</div>
                          <div className="text-sm text-ink-500">{assignment.user_email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-navy-100 text-navy-800">
                          {assignment.role_name}
                        </span>
                      </td>
                      <td className="font-mono tabular-nums px-6 py-4 whitespace-nowrap text-sm text-ink-500">
                        {new Date(assignment.assigned_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setIsAssignModalOpen(true)}
                          className="text-navy-600 hover:text-navy-900"
                        >
                          Change Role
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}

      {/* Create/Edit Role Modal */}
      {(isCreateModalOpen || editingRole) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-card-title text-ink-900 mb-4">
                {editingRole ? 'Edit Role' : 'Create New Role'}
              </h3>

              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Role Name</label>
                    <input
                      type="text"
                      value={roleForm.name}
                      onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                      className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      placeholder="e.g., Department Head"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">Description</label>
                    <input
                      type="text"
                      value={roleForm.description}
                      onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                      className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                      placeholder="Brief description of the role"
                    />
                  </div>
                </div>

                <div>
                  <h4 className="text-md font-medium text-ink-900 mb-3">Permissions</h4>
                  <div className="space-y-4">
                    {Object.entries(getPermissionsByModule()).map(([module, permissions]) => (
                      <div key={module} className="border border-ink-200 rounded-lg p-4">
                        <h5 className="font-medium text-ink-900 mb-3">{module}</h5>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {permissions.map((permission) => (
                            <label key={permission.id} className="flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={roleForm.permissions.includes(permission.id)}
                                onChange={() => togglePermission(permission.id)}
                                className="rounded border-ink-300 text-navy-600 focus:ring-navy-500"
                              />
                              <div>
                                <div className="text-sm font-medium text-ink-700">{permission.name}</div>
                                <div className="text-xs text-ink-500">{permission.description}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setIsCreateModalOpen(false);
                    setEditingRole(null);
                    resetRoleForm();
                  }}
                  className="px-4 py-2 text-ink-700 bg-ink-100 rounded-md hover:bg-ink-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={editingRole ? handleUpdateRole : handleCreateRole}
                  className="px-4 py-2 bg-navy-600 text-white rounded-md hover:bg-navy-700 transition-colors"
                >
                  {editingRole ? 'Update Role' : 'Create Role'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Assign Role Modal */}
      {isAssignModalOpen && (
        <AssignRoleModal
          roles={roles}
          onClose={() => setIsAssignModalOpen(false)}
          onAssign={handleAssignRole}
        />
      )}
    </div>
  );
};

interface AssignRoleModalProps {
  roles: Role[];
  onClose: () => void;
  onAssign: (userId: string, roleId: string) => void;
}

const AssignRoleModal: React.FC<AssignRoleModalProps> = ({ roles, onClose, onAssign }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('staff')
        .select('id, name, email, role')
        .order('name');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedUser && selectedRole) {
      onAssign(selectedUser, selectedRole);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="p-6">
          <h3 className="text-lg font-medium text-ink-900 mb-4">
            Assign Role to User
          </h3>

          {loading ? (
            <div className="flex justify-center py-8">
              <Spinner size="lg" label="Loading…" />
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">User</label>
                <select
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                >
                  <option value="">Select a user</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-700 mb-1">Role</label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-ink-300 rounded-md focus:ring-2 focus:ring-navy-500 focus:border-transparent"
                >
                  <option value="">Select a role</option>
                  {roles.map(role => (
                    <option key={role.id} value={role.name}>
                      {role.name} - {role.description}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-ink-700 bg-ink-100 rounded-md hover:bg-ink-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-navy-600 text-white rounded-md hover:bg-navy-700 transition-colors"
                >
                  Assign Role
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default RoleManagement;